import React, { useState } from 'react';

const VARIABLES = [
  { label: 'First Name', value: '{{first_name}}' },
  { label: 'Last Name', value: '{{last_name}}' },
  { label: 'Full Name', value: '{{first_name}} {{last_name}}' },
  { label: 'Grade', value: '{{grade}}' },
  { label: 'School', value: '{{school_name}}' }
];

const FONTS = [
  'ADAM-CG-PRO.REGULAR.OTF',
  'Montserrat-Bold.ttf',
  'Montserrat-Regular.ttf',
  'Roboto-Bold.ttf',
  'Roboto-Regular.ttf',
  'PlayfairDisplay-Bold.ttf',
  'Oswald-Bold.ttf'
];

const Field = ({ label, children, half }) => (
  <div style={{ marginBottom: 8, display: 'inline-block', width: half ? '48%' : '100%', marginRight: half ? '4%' : 0, verticalAlign: 'top' }}>
    <label style={{ display: 'block', fontSize: 10, color: '#666', marginBottom: 2 }}>{label}</label>
    {children}
  </div>
);

const SmallInput = ({ value, onChange, type = 'text', placeholder, step }) => (
  <input
    type={type}
    value={value ?? ''}
    onChange={e => onChange(type === 'number' ? (e.target.value === '' ? null : Number(e.target.value)) : e.target.value)}
    onKeyDown={type === 'number' ? e => {
      if (e.shiftKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        e.preventDefault();
        const s = parseFloat(e.target.step) || 1;
        const current = parseFloat(e.target.value) || 0;
        const delta = (e.key === 'ArrowUp' ? 1 : -1) * s * 10;
        onChange(Math.round((current + delta) * 1e6) / 1e6);
      }
    } : undefined}
    placeholder={placeholder}
    step={step}
    style={{
      width: '100%', padding: '4px 6px', border: '1px solid #ddd', borderRadius: 3,
      fontSize: 12, boxSizing: 'border-box'
    }}
  />
);

// Displays a decimal (0.12) as percentage (12.00) and converts back on change
const PctInput = ({ value, onChange, placeholder }) => {
  const displayVal = value != null ? Math.round(value * 10000) / 100 : '';
  return (
    <input
      type="number"
      value={displayVal === '' ? '' : displayVal}
      onChange={e => {
        if (e.target.value === '') { onChange(null); return; }
        onChange(Math.round(Number(e.target.value) * 100) / 10000);
      }}
      onKeyDown={e => {
        if (e.shiftKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
          e.preventDefault();
          const current = parseFloat(e.target.value) || 0;
          const delta = (e.key === 'ArrowUp' ? 1 : -1);
          onChange(Math.round((current + delta) * 100) / 10000);
        }
      }}
      placeholder={placeholder}
      step="0.1"
      style={{
        width: '100%', padding: '4px 6px', border: '1px solid #ddd', borderRadius: 3,
        fontSize: 12, boxSizing: 'border-box'
      }}
    />
  );
};

const TextFieldEditor = ({ field, index, onUpdate, onRemove }) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <div style={{
      marginBottom: 12, border: '1px solid #e0e0e0', borderRadius: 4,
      background: '#fff', overflow: 'hidden'
    }}>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          padding: '8px 12px', background: '#f5f5f5', cursor: 'pointer',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 500 }}>
          Field {index + 1}: {field.content || '(empty)'}
        </span>
        <span style={{ fontSize: 11, color: '#999' }}>{expanded ? '▼' : '▶'}</span>
      </div>

      {expanded && (
        <div style={{ padding: 12 }}>
          <Field label="Content">
            <SmallInput value={field.content} onChange={v => onUpdate({ content: v })} placeholder="{{first_name}}" />
          </Field>

          <div style={{ marginBottom: 8 }}>
            <span style={{ fontSize: 10, color: '#666' }}>Insert variable: </span>
            {VARIABLES.map(v => (
              <button
                key={v.value}
                onClick={() => onUpdate({ content: (field.content || '') + v.value })}
                style={{
                  fontSize: 10, padding: '2px 6px', marginRight: 4, marginTop: 2,
                  background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: 3,
                  cursor: 'pointer'
                }}
              >
                {v.label}
              </button>
            ))}
          </div>

          <Field label="Font">
            <select
              value={field.font_filename || ''}
              onChange={e => onUpdate({ font_filename: e.target.value })}
              style={{
                width: '100%', padding: '4px 6px', border: '1px solid #ddd',
                borderRadius: 3, fontSize: 12, background: '#fff', boxSizing: 'border-box'
              }}
            >
              <option value="">Select font...</option>
              {FONTS.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </Field>

          <Field label="Font S3 Path">
            <SmallInput value={field.font_s3_path} onChange={v => onUpdate({ font_s3_path: v })} placeholder="s3://shoobphoto/fonts/" />
          </Field>

          <Field label="Color" half>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <input
                type="color"
                value={field.font_color || '#000000'}
                onChange={e => onUpdate({ font_color: e.target.value })}
                style={{ width: 28, height: 28, border: 'none', cursor: 'pointer' }}
              />
              <SmallInput value={field.font_color} onChange={v => onUpdate({ font_color: v })} />
            </div>
          </Field>

          <Field label="Size Min (pt)" half>
            <SmallInput type="number" value={field.font_size_min} onChange={v => onUpdate({ font_size_min: v })} />
          </Field>
          <Field label="Size Max (pt)" half>
            <SmallInput type="number" value={field.font_size_max} onChange={v => onUpdate({ font_size_max: v })} />
          </Field>

          <Field label="Center X (%)" half>
            <PctInput value={field.center_x} onChange={v => onUpdate({ center_x: v })} placeholder="e.g. 50" />
          </Field>
          <Field label="Center Y (%)" half>
            <PctInput value={field.center_y} onChange={v => onUpdate({ center_y: v })} placeholder="e.g. 85" />
          </Field>

          <Field label="Bound Left (%)" half>
            <PctInput value={field.bound_x_left} onChange={v => onUpdate({ bound_x_left: v })} placeholder="e.g. 4" />
          </Field>
          <Field label="Bound Right (%)" half>
            <PctInput value={field.bound_x_right} onChange={v => onUpdate({ bound_x_right: v })} placeholder="e.g. 96" />
          </Field>

          <Field label="Sizing Rule">
            <select
              value={field.sizing_rule || 'shrink_to_fit'}
              onChange={e => onUpdate({ sizing_rule: e.target.value })}
              style={{
                width: '100%', padding: '4px 6px', border: '1px solid #ddd',
                borderRadius: 3, fontSize: 12, background: '#fff', boxSizing: 'border-box'
              }}
            >
              <option value="shrink_to_fit">Shrink to fit</option>
              <option value="fixed">Fixed size</option>
              <option value="wrap">Wrap text</option>
              <option value="fit_to_bounds_max_first">Fit to bounds (max first)</option>
            </select>
          </Field>

          <Field label="Align">
            <select
              value={field.align || 'center'}
              onChange={e => onUpdate({ align: e.target.value })}
              style={{
                width: '100%', padding: '4px 6px', border: '1px solid #ddd',
                borderRadius: 3, fontSize: 12, background: '#fff', boxSizing: 'border-box'
              }}
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </Field>

          <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
            <label style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
              <input type="checkbox" checked={field.bold || false} onChange={e => onUpdate({ bold: e.target.checked })} />
              Bold
            </label>
            <label style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
              <input type="checkbox" checked={field.italic || false} onChange={e => onUpdate({ italic: e.target.checked })} />
              Italic
            </label>
            <label style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
              <input type="checkbox" checked={field.shadow || false} onChange={e => onUpdate({ shadow: e.target.checked })} />
              Shadow
            </label>
          </div>

          <button
            onClick={onRemove}
            style={{
              marginTop: 8, padding: '4px 12px', fontSize: 11,
              color: '#d32f2f', background: '#ffebee', border: '1px solid #ef9a9a',
              borderRadius: 3, cursor: 'pointer'
            }}
          >
            Remove Field
          </button>
        </div>
      )}
    </div>
  );
};

const TextTab = ({ textFields, onUpdate, onAdd, onRemove }) => {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h4 style={{ margin: 0, fontSize: 14, color: '#333' }}>Text Fields</h4>
        <button
          onClick={onAdd}
          style={{
            padding: '4px 12px', fontSize: 12,
            background: '#1976d2', color: '#fff', border: 'none',
            borderRadius: 3, cursor: 'pointer'
          }}
        >
          + Add Field
        </button>
      </div>

      {textFields.length === 0 && (
        <div style={{ padding: 20, textAlign: 'center', color: '#999', fontSize: 13 }}>
          No text fields. Click "+ Add Field" to add one.
        </div>
      )}

      {textFields.map((field, i) => (
        <TextFieldEditor
          key={field.id || `new-${i}`}
          field={field}
          index={i}
          onUpdate={(changes) => onUpdate(i, changes)}
          onRemove={() => onRemove(i)}
        />
      ))}
    </div>
  );
};

export default TextTab;
